import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import colors from '../../styled-components/palette';
import { OutcomeStatus, OutcomeStatuses } from '../../types/history';

const ColorCodeByStatus = Object.freeze({
  [OutcomeStatuses.WON]: colors.greenwinamax350,
  [OutcomeStatuses.LOST]: colors.redwinamax350,
  [OutcomeStatuses.CANCELLED]: colors.bluegrey500,
  [OutcomeStatuses.RUNNING]: colors.yellowwinamax350,
  [OutcomeStatuses.LIVE]: colors.yellowwinamax350,
});

const Colors = Object.freeze({
  progressLineBase: colors.bluegrey800,
  thresholdPlaceholderTick: colors.bluegrey900,
  thresholdLabel: colors.white,
  currentValueLabel: colors.bluegrey950,
});

type WrapperProps = {
  $status: OutcomeStatus;
};
const Wrapper = styled.div<WrapperProps>`
  --HistoryItemOutcomeCardProgressBar-wrapperHeight: 40px;
  --HistoryItemOutcomeCardProgressBar-lineHeight: 3px;

  position: relative;
  width: 100%;
  height: var(--HistoryItemOutcomeCardProgressBar-wrapperHeight);

  ${({ $status }) => css`
    --HistoryItemOutcomeCardProgressBar-mainColor: ${ColorCodeByStatus[$status]};
  `}
`;

const Rail = styled.div`
  position: absolute;
  width: 100%;
  height: var(--HistoryItemOutcomeCardProgressBar-lineHeight);
  top: 50%;
  left: 0;
  transform: translateY(-50%);

  border-radius: 3em;
`;

type LeftLineProps = {
  $progressRatio: number;
};
const LeftLine = styled.div<LeftLineProps>`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  height: var(--HistoryItemOutcomeCardProgressBar-lineHeight);
  background-color: var(--HistoryItemOutcomeCardProgressBar-mainColor);
  border-top-left-radius: inherit;
  border-bottom-left-radius: inherit;
  z-index: 0;

  ${({ $progressRatio }) => css`
    width: calc(
      ${$progressRatio * 100}% - calc(var(--HistoryItemOutcomeCardProgressBar-currentValuePlaceHolderWidth) / 2)
    );
  `}
`;

type CurrentValuePlaceHolderProps = {
  $progressRatio: number;
};
const CurrentValuePlaceHolder = styled.div<CurrentValuePlaceHolderProps>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  padding: 0 0.2em;
  z-index: 2;

  ${({ $progressRatio }) => css`
    left: calc(
      ${$progressRatio * 100}% - calc(var(--HistoryItemOutcomeCardProgressBar-currentValuePlaceHolderWidth) / 2)
    );
  `}
`;

const CurrentValueLabel = styled.div`
  background-color: var(--HistoryItemOutcomeCardProgressBar-mainColor);
  color: ${Colors.currentValueLabel};
  padding: 0.1em 0.3em;
  font-size: 0.9em;
  border-radius: 5px;
  font-weight: bold;
`;

type RightLineProps = {
  $progressRatio: number;
};
const RightLine = styled.div<RightLineProps>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: var(--HistoryItemOutcomeCardProgressBar-lineHeight);
  background-color: ${Colors.progressLineBase};

  border-top-right-radius: inherit;
  border-bottom-right-radius: inherit;
  z-index: 0;

  ${({ $progressRatio }) => css`
    --HistoryItemOutcomeCardProgressBar-rightLineLeft: calc(
      ${$progressRatio * 100}% + calc(var(--HistoryItemOutcomeCardProgressBar-currentValuePlaceHolderWidth) / 2)
    );
    left: var(--HistoryItemOutcomeCardProgressBar-rightLineLeft);
    width: calc(100% - var(--HistoryItemOutcomeCardProgressBar-rightLineLeft));
  `}
`;

const ThresholdPlaceholder = styled.div`
  position: absolute;
  width: 0.3em;
  height: var(--HistoryItemOutcomeCardProgressBar-lineHeight);
  left: 75%;
  top: 50%;
  transform: translate(-50%, -50%);
  right: 0;
  font-size: 0.8em;
  background-color: ${Colors.thresholdPlaceholderTick};

  z-index: 1;
`;

const ThresholdLabel = styled.div`
  position: absolute;
  left: 0%;
  top: 100%;
  transform: translateX(-50%);
  padding: 0 0.2em;

  color: ${Colors.thresholdLabel};
`;

type HistoryItemOutcomeCardProgressBarProps = {
  className?: string;
  threshold?: number;
  currentValue?: number;
  status: OutcomeStatus;
};

const HistoryItemOutcomeCardProgressBar = React.forwardRef<HTMLDivElement, HistoryItemOutcomeCardProgressBarProps>(
  function HistoryItemOutcomeCardProgressBar(
    { className = '', threshold = -1, currentValue = -1, status }: HistoryItemOutcomeCardProgressBarProps,
    ref,
  ) {
    const wrapperNodeRef = useRef<HTMLDivElement | null>(null);
    const currentValuePlaceHolderNodeRef = useRef<HTMLDivElement>(null);
    const thresholdLabelNodeRef = useRef<HTMLDivElement>(null);

    const propsAreInvalid = threshold === -1 || currentValue === -1;
    const [progressRatio, setProgressRatio] = useState(0);

    useLayoutEffect(() => {
      const currentValuePlaceHolderNode = currentValuePlaceHolderNodeRef.current;
      const wrapperNode = wrapperNodeRef.current;

      if (currentValuePlaceHolderNode && wrapperNode) {
        const { width: currentValuePlaceHolderNodeWidth } = currentValuePlaceHolderNode.getBoundingClientRect();
        wrapperNode.style.setProperty(
          '--HistoryItemOutcomeCardProgressBar-currentValuePlaceHolderWidth',
          `${currentValuePlaceHolderNodeWidth.toFixed(2)}px`,
        );
      }
    }, [threshold]);

    useEffect(() => {
      /**
       * This effect is used to update the progressRatio when the currentValue or threshold changes
       **/
      const wrapperNode = wrapperNodeRef.current;
      const currentValuePlaceHolderNode = currentValuePlaceHolderNodeRef.current;
      const thresholdLabelNode = thresholdLabelNodeRef.current;

      if (propsAreInvalid || !currentValuePlaceHolderNode || !thresholdLabelNode || !wrapperNode) {
        setProgressRatio(0);
        return;
      }

      const { width: wrapperNodeNodeWidth } = wrapperNode.getBoundingClientRect();
      const { width: currentValuePlaceHolderNodeWidth } = currentValuePlaceHolderNode.getBoundingClientRect();
      const { width: thresholdLabelNodeWidth } = thresholdLabelNode.getBoundingClientRect();

      let nextRatio = currentValue / Math.round(threshold / 0.75);

      const supposedCurrentValueNodeLeftBorderX =
        nextRatio * wrapperNodeNodeWidth - currentValuePlaceHolderNodeWidth / 2;
      const supposedCurrentValueNodeRightBorderX =
        nextRatio * wrapperNodeNodeWidth + currentValuePlaceHolderNodeWidth / 2;
      const thresholdX = wrapperNodeNodeWidth * 0.75;
      const thresholdLabelNodeLeftBorderX = thresholdX - thresholdLabelNodeWidth / 2;
      const thresholdLabelNodeRightBorderX = thresholdX + thresholdLabelNodeWidth / 2;

      /**
       * The following conditions are used to prevent the currentValuePlaceHolderNode from going out of the wrapperNode
       * or overlapping with the thresholdLabelNode
       **/
      if (supposedCurrentValueNodeLeftBorderX < 0) {
        // too much on the left
        nextRatio = currentValuePlaceHolderNodeWidth / 2 / wrapperNodeNodeWidth;
      } else if (nextRatio <= 0.75 && supposedCurrentValueNodeRightBorderX >= thresholdLabelNodeLeftBorderX) {
        // overlaping with the threshold label left border
        nextRatio =
          (thresholdLabelNodeLeftBorderX - currentValuePlaceHolderNodeWidth / 2) / wrapperNodeNodeWidth - 0.01;
      } else if (nextRatio >= 0.75 && supposedCurrentValueNodeLeftBorderX <= thresholdLabelNodeRightBorderX) {
        // overlaping with the threshold label right border
        nextRatio = (thresholdLabelNodeRightBorderX + currentValuePlaceHolderNodeWidth / 2) / wrapperNodeNodeWidth;
      } else if (supposedCurrentValueNodeRightBorderX > wrapperNodeNodeWidth) {
        // too much on the right
        nextRatio = (wrapperNodeNodeWidth - currentValuePlaceHolderNodeWidth / 2) / wrapperNodeNodeWidth;
      }

      setProgressRatio(nextRatio);
    }, [currentValue, propsAreInvalid, threshold]);

    return (
      <Wrapper
        ref={(thisRef) => {
          wrapperNodeRef.current = thisRef;

          if (typeof ref === 'function') {
            ref(thisRef);
          }
        }}
        className={className}
        $status={status}
      >
        <Rail>
          <LeftLine $progressRatio={progressRatio} />
          <CurrentValuePlaceHolder ref={currentValuePlaceHolderNodeRef} $progressRatio={progressRatio}>
            <CurrentValueLabel>{currentValue}</CurrentValueLabel>
          </CurrentValuePlaceHolder>
          <RightLine $progressRatio={progressRatio} />
          <ThresholdPlaceholder>
            <ThresholdLabel ref={thresholdLabelNodeRef}>{threshold}</ThresholdLabel>
          </ThresholdPlaceholder>
        </Rail>
      </Wrapper>
    );
  },
);

export default React.memo(HistoryItemOutcomeCardProgressBar);
